import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Truck,
  Package,
  RefreshCw,
  MoreVertical,
  Eye } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusConfig = {
  draft: { label: 'Draft', variant: 'outline', icon: FileText },
  issued: { label: 'Issued', variant: 'secondary', icon: Send },
  sent_to_supplier: { label: 'Sent', variant: 'default', icon: Truck },
  supplier_confirmed: { label: 'Confirmed', variant: 'default', icon: CheckCircle2 },
  partially_received: { label: 'Partial', variant: 'secondary', icon: Package },
  received: { label: 'Received', variant: 'default', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', variant: 'destructive', icon: XCircle }
};

export const PurchaseOrders = () => {const { t } = useTranslation("procurement");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/procurement/orders`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch orders');
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      toast.error('Failed to load purchase orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleAction = async (poId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/orders/${poId}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`Failed to ${action}`);
      toast.success(`Order ${action} successfully`);
      fetchOrders();
    } catch (err) {
      toast.error(`Failed to ${action} order`);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch = (order.po_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.vendor_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: orders.length,
    draft: orders.filter((o) => o.status === 'draft').length,
    issued: orders.filter((o) => o.status === 'issued').length,
    inTransit: orders.filter((o) => ['sent_to_supplier', 'supplier_confirmed'].includes(o.status)).length,
    received: orders.filter((o) => o.status === 'received').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="purchase-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("purchaseOrders", "Purchase Orders")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageAndTrack", "Manage and track purchase orders")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchOrders} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4" />{t("newPo", "New PO")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">{t("total", "Total")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-400">{stats.draft}</p>
              <p className="text-xs text-muted-foreground">{t("draft", "Draft")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-500">{stats.issued}</p>
              <p className="text-xs text-muted-foreground">{t("issued", "Issued")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-500">{stats.inTransit}</p>
              <p className="text-xs text-muted-foreground">{t("inTransit", "In Transit")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-500">{stats.received}</p>
              <p className="text-xs text-muted-foreground">{t("received", "Received")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by PO number or vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9" />
          
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'draft', 'issued', 'sent_to_supplier', 'received'].map((status) =>
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => setStatusFilter(status)}
            size="sm"
            className="capitalize">
            
              {status === 'sent_to_supplier' ? 'In Transit' : status}
            </Button>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {filteredOrders.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 text-slate-600 mb-4" />
              <p className="text-slate-400 text-lg">{t("noPurchaseOrders", "No purchase orders found")}</p>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("poNumber", "PO Number")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("date", "Date")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("items", "Items")}</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("status", "Status")}</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                  const config = statusConfig[order.status] || statusConfig.draft;
                  const StatusIcon = config.icon;
                  return (
                    <tr key={order.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="p-4">
                          <span className="font-mono font-medium">{order.po_number}</span>
                        </td>
                        <td className="p-4">{order.vendor_name || 'N/A'}</td>
                        <td className="p-4 text-muted-foreground">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-4">{order.item_count || 0}{t("items", "items")}</td>
                        <td className="p-4 text-right font-medium">
                          ${(order.total_amount || 0).toLocaleString()}
                        </td>
                        <td className="p-4">
                          <Badge variant={config.variant} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {config.label}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex gap-2 justify-end">
                            {order.status === 'draft' &&
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAction(order.id, 'issue')}>{t("issue", "Issue")}


                          </Button>
                          }
                            {order.status === 'issued' &&
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAction(order.id, 'send-to-supplier')}>{t("send", "Send")}


                          </Button>
                          }
                            <Button size="sm" variant="ghost">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};

export default PurchaseOrders;